import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { intuitColors } from '../chartColors';

const title = 'Electronics Forecast: September 2025';

export const stackedCategoryTop3 = (chartDivId: string) => {
	const root = am5.Root.new(chartDivId);
	root.setThemes([am5themes_Animated.new(root)]);

	const chart = root.container.children.push(
		am5xy.XYChart.new(root, {
			layout: root.verticalLayout,
			panX: false,
			panY: false,
			wheelX: 'none',
			wheelY: 'none',
		})
	);

	chart.children.unshift(
		am5.Label.new(root, {
			text: title,
			fontSize: 18,
			fontWeight: '700',
			x: 0,
			centerX: 0,
			paddingBottom: 28,
			fill: am5.color(0xffffff), // title white
		})
	);

	const data = [
		{
			category: 'Games',
			top1: 420,
			top1Name: 'Pikmin 4',
			top2: 300,
			top2Name: 'Mario Kart 8',
			top3: 180,
			top3Name: 'Zelda: TotK',
			others: 600,
		},
		{
			category: 'Accessories',
			top1: 350,
			top1Name: 'Switch Pro Controller',
			top2: 220,
			top2Name: 'Headset X',
			top3: 160,
			top3Name: '4K HDMI',
			others: 400,
		},
		{
			category: 'Consoles',
			top1: 800,
			top1Name: 'Nintendo Switch 2',
			top2: 700,
			top2Name: 'PlayStation 5',
			top3: 500,
			top3Name: 'Xbox One',
			others: 300,
		},
	];

	const xAxis = chart.xAxes.push(
		am5xy.CategoryAxis.new(root, {
			categoryField: 'category',
			renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 30 }),
		})
	);
	xAxis.data.setAll(data);
	// make axis labels white
	xAxis.get('renderer').labels.template.setAll({ fill: am5.color(0xffffff) });

	const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) }));
	yAxis.get('renderer').labels.template.setAll({ fill: am5.color(0xffffff) });

	const makeStack = (valueField: keyof (typeof data)[number], labelField: keyof (typeof data)[number] | null, name: string, fill: am5.Color) => {
		// pick white/black for good contrast against this stack’s fill
		const pickLabelColor = (c: am5.Color) => {
			const luminance = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
			return luminance > 0.6 ? am5.color(0x000000) : am5.color(0xffffff);
		};
		const labelFill = pickLabelColor(fill);

		const series = chart.series.push(
			am5xy.ColumnSeries.new(root, {
				name,
				xAxis,
				yAxis,
				valueYField: valueField as string,
				categoryXField: 'category',
				stacked: true,
				tooltip: am5.Tooltip.new(root, {
					labelText: "{name}\n{categoryX}: {valueY.formatNumber('#,###')}",
				}),
			})
		);

		series.columns.template.setAll({
			width: am5.percent(80),
			fill,
			stroke: fill,
			cornerRadiusTL: 4,
			cornerRadiusTR: 4,
		});

		// In-segment labels with auto-contrast color
		series.bullets.push(() =>
			am5.Bullet.new(root, {
				sprite: am5.Label.new(root, {
					centerX: am5.p50,
					centerY: am5.p50,
					populateText: true,
					text: labelField ? `{${labelField as string}}: {valueY.formatNumber('#,###')}` : `Other: {valueY.formatNumber('#,###')}`,
					fill: '#666',
					fontSize: 12,
					maxWidth: 400,
					oversizedBehavior: 'truncate',
					textAlign: 'center',
				}),
			})
		);

		series.data.setAll(data);
		return series;
	};
	// Bottom → Top order so Top 1 appears ON TOP
	makeStack('others', null, 'Others', intuitColors.blueberry);
	makeStack('top3', 'top3Name', 'Top 3', intuitColors.persimmon);
	makeStack('top2', 'top2Name', 'Top 2', intuitColors.agave);
	makeStack('top1', 'top1Name', 'Top 1', intuitColors.kiwi);

	chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));

	return () => root.dispose();
};
